/**
 * storyService — Firebase Cloud Function callables + thin wrappers.
 *
 * Pages must NOT import `httpsCallable` directly. Callable invocations flow
 * through here. **`moderateStory`** updates `km_stories` via Supabase (`storyRepo`)
 * so the moderation dashboard avoids Firebase callable CORS / Cloud Run preflight.
 *
 * Submit, edit, report, and backfill still use Firebase (`ensureSignedIn`).
 * Hybrid search uses the Supabase Edge Function `km-search-stories`.
 */

import { moderateStoryStatus } from '@/services/storyRepo';
import { httpsCallable } from 'firebase/functions';
import { FunctionRegion } from '@supabase/supabase-js';
import { firebaseFunctions } from '@/lib/firebase';
import { firebaseAuthBridge } from '@/lib/firebaseAuthBridge';
import { supabase } from '@/lib/supabase';
import { toSearchResultDto } from '@/lib/dto';

const call = (name) => httpsCallable(firebaseFunctions, name);

// Long-running callables: backfill embeddings + LLM re-extraction can
// each take well over the Firebase default 70s on a single batch.
// Server-side timeout is 540s, match that on the client so the user
// doesn't see a spurious DEADLINE_EXCEEDED while the function is still
// actually running. Pages still chunk into multiple cursor-paged calls
// for very large corpora — this timeout is the upper bound per call.
const LONG_CALL_TIMEOUT_MS = 540_000;
const callLong = (name) =>
  httpsCallable(firebaseFunctions, name, { timeout: LONG_CALL_TIMEOUT_MS });

const ensureSignedIn = async () => {
  try {
    await firebaseAuthBridge.syncWithSupabase();
  } catch (e) {
    // Surface bridge errors verbatim — toast in caller will show it.
    throw new Error(e.message || 'Sign-in failed. Please try again.');
  }
  if (!firebaseAuthBridge.isFirebaseSynced()) {
    throw new Error('Sign-in required. Please log in and try again.');
  }
};

/**
 * Submit a story.
 *   path: 'free_flow' | 'structured' (legacy A/B/C still accepted by the function)
 *   free_flow:  { raw_text, story_type, ... }
 *   structured: { sections: { section_1, ..., section_5 }, story_type, title, ... }
 */
export async function processStory(payload) {
  await ensureSignedIn();
  const { data } = await call('km_process_story')(payload);
  return data;
}

/**
 * Persist creator edits (`km_update_story`). Allowed keys are enforced in
 * Cloud Functions; typical UI payloads use title, raw_text or section_1..5,
 * is_anonymous, and author_display_name.
 */
export async function updateStory(storyId, updates) {
  await ensureSignedIn();
  const { data } = await call('km_update_story')({ story_id: storyId, updates });
  return data;
}

/**
 * Re-run Gemini against an existing story and return fresh suggestions
 * (`{ story_id, suggestions: { title, highlight, life_areas, ... } }`).
 * Does NOT write to the DB — caller drops the suggestions into review-screen
 * state, then saves explicitly via updateStory.
 */
export async function reanalyzeStory(storyId) {
  await ensureSignedIn();
  const { data } = await call('km_reanalyze_story')({ story_id: storyId });
  return data;
}

/**
 * Public hybrid search (vector + FTS + RRF). No auth needed.
 *
 * Invokes Supabase Edge Function `km-search-stories` pinned to ap-south-1
 * (Mumbai) near the database.
 *
 * `filters` is optional: { life_areas?, inner_knots?, obstacles?,
 * mental_states?, inner_qualities?, guna_arc? } — each an array of tag IDs
 * that the result must overlap with (AND across dimensions, OR within each).
 *
 * `impliedTags` is optional: tag slugs the caller inferred from the free-text
 * query (via the typeahead alias matcher). A SOFT signal — forwarded to the
 * RPC as `implied_tag_ids`, where it widens recall / reorders but never
 * filters. Separate lane from the hard `filters` above. See migration 034.
 *
 * Returns array of UI-shaped DTOs with similarity_score, plus optional
 * `debug_timing_ms` from the Edge Function (embed_ms, rpc_ms, total_ms).
 */
export async function searchStories(query, options = {}) {
  const { limit = 10, filters, impliedTags } = options;
  const payload = { query, limit };
  if (filters && Object.keys(filters).length) payload.filters = filters;
  if (Array.isArray(impliedTags) && impliedTags.length) payload.implied_tags = impliedTags;

  const { data, error } = await supabase.functions.invoke('km-search-stories', {
    body: payload,
    region: FunctionRegion.ApSouth1,
  });
  if (error) throw new Error(error.message || 'Search failed');

  const stories = (data?.stories || []).map(toSearchResultDto);
  const timing = data?.debug_timing_ms || null;
  const toMs = (v) => {
    if (v == null || v === '') return null;
    const x = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(x) ? Math.round(x) : null;
  };
  return {
    query: data?.query || query,
    count: stories.length,
    stories,
    /** Edge Function breakdown: Gemini embed vs Supabase RPC (server-measured). */
    debug_timing_ms: timing && typeof timing === 'object'
      ? {
          embed_ms: toMs(timing.embed_ms),
          rpc_ms: toMs(timing.rpc_ms),
          total_ms: toMs(timing.total_ms),
        }
      : null,
  };
}

export async function moderateStory(storyId, action, notes) {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session?.user?.id) {
    throw new Error('Sign in required.');
  }
  return moderateStoryStatus(storyId, action, notes || '');
}

export async function ingestYoutube(url, options = {}) {
  await ensureSignedIn();
  const { data } = await call('km_ingest_youtube')({
    url,
    story_type: options.story_type || 'personal',
    language: options.language || 'en',
  });
  return data;
}

export async function reportStory(storyId, reason, details) {
  await ensureSignedIn();
  const { data } = await call('km_report_story')({
    story_id: storyId,
    reason,
    details: details || '',
  });
  return data;
}

export async function backfillEmbeddings() {
  await ensureSignedIn();
  const { data } = await callLong('km_backfill_embeddings')({});
  return data;
}

// Re-run Gemini tag extraction on a batch of approved stories,
// overwriting ONLY the fields the caller specifies. Every other
// column — including any manually-curated practice_ids, custom
// life_areas, etc. — stays exactly as it was in the row.
//
// `fields` is required and must be a non-empty subset of:
//   life_areas, inner_knots, obstacles, mental_states,
//   inner_qualities, guna_arc, tradition, practice_ids,
//   practice_freetext, time_period, title, highlight
//
// Returns { processed, updated, failed, next_cursor, fields }. Caller
// pages through the corpus by re-invoking with the returned
// next_cursor until it's null.
// Default batch size sized for the LLM: ~3-5s per story × 10 = 30-50s.
// Comfortably under the 540s budget with headroom for variance. Smaller
// batches also mean less re-work if any single call does fail.
export const REANALYZE_BATCH_SIZE = 10;

export async function reanalyzeAllStories({
  fields,
  limit = REANALYZE_BATCH_SIZE,
  beforeCreatedAt = null,
  rebuildChunks = false,
} = {}) {
  if (!Array.isArray(fields) || fields.length === 0) {
    throw new Error('reanalyzeAllStories: `fields` must be a non-empty array.');
  }
  await ensureSignedIn();
  const payload = { fields, limit };
  if (beforeCreatedAt) payload.before_created_at = beforeCreatedAt;
  if (rebuildChunks) payload.rebuild_chunks = true;
  const { data } = await callLong('km_reanalyze_all_stories')(payload);
  return data;
}

// Moderator-only single-story variant. Same field-selective semantics
// as reanalyzeAllStories (manual curation in other columns survives),
// scoped to one story_id. Used by the story-view page admin action.
//
// SAFETY: an older deployed function (pre-cbb1ff9) silently ignores
// the story_id field and runs the full batch. We assert the response
// echoes `scoped_to === 'story_id'` so a stale deploy is caught loudly
// here, *after* the call, instead of corrupting the whole corpus
// quietly. If you see this throw: redeploy functions.
export async function reanalyzeOneStory(storyId, { fields, rebuildChunks = false } = {}) {
  if (!storyId) {
    throw new Error('reanalyzeOneStory: storyId is required.');
  }
  if (!Array.isArray(fields) || fields.length === 0) {
    throw new Error('reanalyzeOneStory: `fields` must be a non-empty array.');
  }
  await ensureSignedIn();
  const payload = { story_id: storyId, fields };
  if (rebuildChunks) payload.rebuild_chunks = true;
  const { data } = await callLong('km_reanalyze_all_stories')(payload);
  if (data?.scoped_to !== 'story_id') {
    throw new Error(
      'Single-story reanalyze landed on the batch path — the deployed '
      + 'Cloud Function is out of date. Redeploy functions and try again.',
    );
  }
  return data;
}
